import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSkinSession } from '../../salon/context/SkinSessionContext';
import { SESSION_APP_DATA_BASE } from '../../salon/config/sessionAppData';
import { SALON_ORG_PLAN_KEY } from '../../salon/config/salonApp';
import { fetchWithTimeout } from '../../salon/utils/fetchWithTimeout';
import { appendFranchiseThemeParams } from '../../shared/franchiseFlowAuth';
import {
  getMakeupHairPlanFlags,
  normalizeOrgPlan,
} from '../config/planFeatures';

const OrgPlanContext = createContext(null);

function readStoredPlan() {
  try {
    return normalizeOrgPlan(sessionStorage.getItem(SALON_ORG_PLAN_KEY));
  } catch (_) {
    return 'standard';
  }
}

export function OrgPlanProvider({ children }) {
  const { token } = useSkinSession();
  const [plan, setPlan] = useState(readStoredPlan);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!token?.trim()) {
      setIsReady(true);
      return undefined;
    }

    let cancelled = false;
    const qs = appendFranchiseThemeParams({ _: String(Date.now()) });
    const url = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/page-setup?${qs}`;

    fetchWithTimeout(url, { timeoutMs: 20000 })
      .then((response) => (response.ok ? response.json() : null))
      .then((result) => {
        if (cancelled || !result) return;
        const externalFields =
          result?.external_fields ||
          result?.data?.external_fields ||
          (Array.isArray(result) && result[0]?.external_fields);
        const normalized = normalizeOrgPlan(externalFields?.plan);
        setPlan(normalized);
        try {
          sessionStorage.setItem(SALON_ORG_PLAN_KEY, normalized);
        } catch (_) {}
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const flags = useMemo(() => getMakeupHairPlanFlags(plan), [plan]);

  return (
    <OrgPlanContext.Provider value={{ plan, flags, isReady }}>
      {children}
    </OrgPlanContext.Provider>
  );
}

export function useOrgPlan() {
  const context = useContext(OrgPlanContext);
  if (!context) {
    return {
      plan: 'standard',
      flags: getMakeupHairPlanFlags('standard'),
      isReady: true,
    };
  }
  return context;
}
